/**
 * Test Tech API client. Lightweight axios wrapper that pulls the TEST_TECH
 * JWT from localStorage. Mirrors the shape of `csuiteApi.js` so the two
 * personas remain consistent.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cap_tt_token";

export const ttAuth = {
  get: () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } },
  set: (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch { /* noop */ } },
  clear: () => { try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ } },
};

const client = axios.create({ baseURL: API, timeout: 20000 });
client.interceptors.request.use((cfg) => {
  const t = ttAuth.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const err = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const wrap = (p) => p.then(r => r.data).catch(e => Promise.reject(new Error(err(e))));

export const ttApi = {
  // Auth
  login:    (username, password) => wrap(client.post("/test-tech/login", { username, password })),
  me:       () => wrap(client.get("/test-tech/me")),

  // Programs
  programs: () => wrap(client.get("/test-tech/programs")),

  // Strategy
  getStrategy: (pid) => wrap(client.get(`/test-tech/programs/${pid}/strategy`)),
  putStrategy: (pid, body) => wrap(client.put(`/test-tech/programs/${pid}/strategy`, body)),

  // Scenarios
  listScenarios:  (pid) => wrap(client.get(`/test-tech/programs/${pid}/scenarios`)),
  createScenario: (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/scenarios`, b)),
  updateScenario: (pid, sid, b) => wrap(client.put(`/test-tech/programs/${pid}/scenarios/${sid}`, b)),
  deleteScenario: (pid, sid) => wrap(client.delete(`/test-tech/programs/${pid}/scenarios/${sid}`)),

  // Cases
  listCases:   (pid) => wrap(client.get(`/test-tech/programs/${pid}/cases`)),
  createCase:  (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/cases`, b)),
  updateCase:  (pid, cid, b) => wrap(client.put(`/test-tech/programs/${pid}/cases/${cid}`, b)),
  deleteCase:  (pid, cid) => wrap(client.delete(`/test-tech/programs/${pid}/cases/${cid}`)),
  executeCase: (pid, cid, b) => wrap(client.post(`/test-tech/programs/${pid}/cases/${cid}/execute`, b)),

  // Defects
  listDefects:  (pid) => wrap(client.get(`/test-tech/programs/${pid}/defects`)),
  createDefect: (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/defects`, b)),
  updateDefect: (pid, did, b) => wrap(client.put(`/test-tech/programs/${pid}/defects/${did}`, b)),
  deleteDefect: (pid, did) => wrap(client.delete(`/test-tech/programs/${pid}/defects/${did}`)),

  // KPIs
  kpis: (pid) => wrap(client.get(`/test-tech/programs/${pid}/kpis`)),

  // Requirements (RTM)
  listReqs:    (pid) => wrap(client.get(`/test-tech/programs/${pid}/requirements`)),
  createReq:   (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/requirements`, b)),
  updateReq:   (pid, rid, b) => wrap(client.put(`/test-tech/programs/${pid}/requirements/${rid}`, b)),
  deleteReq:   (pid, rid) => wrap(client.delete(`/test-tech/programs/${pid}/requirements/${rid}`)),
  rtm:         (pid) => wrap(client.get(`/test-tech/programs/${pid}/rtm`)),

  // Environments (Readiness)
  listEnvs:    (pid) => wrap(client.get(`/test-tech/programs/${pid}/environments`)),
  createEnv:   (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/environments`, b)),
  updateEnv:   (pid, eid, b) => wrap(client.put(`/test-tech/programs/${pid}/environments/${eid}`, b)),
  deleteEnv:   (pid, eid) => wrap(client.delete(`/test-tech/programs/${pid}/environments/${eid}`)),

  // Cutover
  listCutover: (pid) => wrap(client.get(`/test-tech/programs/${pid}/cutover`)),
  createCut:   (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/cutover`, b)),
  updateCut:   (pid, tid, b) => wrap(client.put(`/test-tech/programs/${pid}/cutover/${tid}`, b)),
  deleteCut:   (pid, tid) => wrap(client.delete(`/test-tech/programs/${pid}/cutover/${tid}`)),

  // Hypercare incidents
  listIncs:    (pid) => wrap(client.get(`/test-tech/programs/${pid}/incidents`)),
  createInc:   (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/incidents`, b)),
  updateInc:   (pid, iid, b) => wrap(client.put(`/test-tech/programs/${pid}/incidents/${iid}`, b)),
  deleteInc:   (pid, iid) => wrap(client.delete(`/test-tech/programs/${pid}/incidents/${iid}`)),
  warRoom:     (pid) => wrap(client.get(`/test-tech/programs/${pid}/war-room`)),

  // Test Data Management (TDC)
  listTdc:     (pid) => wrap(client.get(`/test-tech/programs/${pid}/test-data`)),
  createTdc:   (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/test-data`, b)),
  updateTdc:   (pid, tid, b) => wrap(client.put(`/test-tech/programs/${pid}/test-data/${tid}`, b)),
  deleteTdc:   (pid, tid) => wrap(client.delete(`/test-tech/programs/${pid}/test-data/${tid}`)),
  refreshTdc:  (pid, tid) => wrap(client.post(`/test-tech/programs/${pid}/test-data/${tid}/refresh`)),
  tdcKpis:     (pid) => wrap(client.get(`/test-tech/programs/${pid}/test-data/kpis`)),

  // System Integration Testing (SIT)
  listSit:     (pid) => wrap(client.get(`/test-tech/programs/${pid}/sit`)),
  createSit:   (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/sit`, b)),
  updateSit:   (pid, sid, b) => wrap(client.put(`/test-tech/programs/${pid}/sit/${sid}`, b)),
  deleteSit:   (pid, sid) => wrap(client.delete(`/test-tech/programs/${pid}/sit/${sid}`)),
  runSit:      (pid, sid, b) => wrap(client.post(`/test-tech/programs/${pid}/sit/${sid}/run`, b)),
  sitKpis:     (pid) => wrap(client.get(`/test-tech/programs/${pid}/sit/kpis`)),

  // Go-Live Scorecard
  listGoLive:    (pid) => wrap(client.get(`/test-tech/programs/${pid}/golive`)),
  createGoLive:  (pid, b) => wrap(client.post(`/test-tech/programs/${pid}/golive`, b)),
  updateGoLive:  (pid, gid, b) => wrap(client.put(`/test-tech/programs/${pid}/golive/${gid}`, b)),
  deleteGoLive:  (pid, gid) => wrap(client.delete(`/test-tech/programs/${pid}/golive/${gid}`)),
  goLiveDecision: (pid) => wrap(client.get(`/test-tech/programs/${pid}/golive/decision`)),

  // AI Risk Agents
  aiGoLiveRisk:       (pid) => wrap(client.post(`/test-tech/programs/${pid}/ai/go-live-risk`)),
  aiTestCoverage:     (pid) => wrap(client.post(`/test-tech/programs/${pid}/ai/test-coverage`)),
  aiDefectIntel:      (pid) => wrap(client.post(`/test-tech/programs/${pid}/ai/defect-intelligence`)),

  // Unified snapshot + Admin bridge
  adminLogin:     (email, password) => wrap(axios.post(`${API}/testing-admin/login`, { email, password })),
  unified:        (pid) => wrap(client.get(`/test-tech/programs/${pid}/unified`)),
  exportPdfUrl:   (pid) => `${API}/test-tech/programs/${pid}/export/pdf`,
  exportPptxUrl:  (pid) => `${API}/test-tech/programs/${pid}/export/pptx`,
};

// Admin endpoints use the CSuite super-admin JWT (via existing csuiteApi.client).
// We rebuild a tiny admin client that reads the csuite token.
const adminClient = axios.create({ baseURL: API, timeout: 20000 });
adminClient.interceptors.request.use((cfg) => {
  try {
    const t = localStorage.getItem("pmv5_csuite_token");
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
  } catch { /* noop */ }
  return cfg;
});

export const ttAdminApi = {
  listTechs:   () => wrap(adminClient.get("/admin/test-techs")),
  createTech:  (b) => wrap(adminClient.post("/admin/test-techs", b)),
  updateTech:  (id, b) => wrap(adminClient.put(`/admin/test-techs/${id}`, b)),
  deleteTech:  (id) => wrap(adminClient.delete(`/admin/test-techs/${id}`)),
  createProgram: (b) => wrap(adminClient.post("/test-tech/programs", b)),
};
